import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Initials } from "./people/bits";

const MATCH = {
  link: "Will link to existing Care person",
  review: "Needs review after import",
  linked: "Already in Care",
};

const Stat = ({ n, label, testid }) => (
  <div data-testid={testid}><p className="t-heading">{n}</p><p className="t-meta">{label}</p></div>
);

const PersonRow = ({ p }) => (
  <div className="py-3 flex items-center gap-3" data-testid={`pco-preview-person-${p.external_id}`}>
    <Initials p={p} size={36} />
    <div className="min-w-0 flex-1">
      <p className="t-body truncate">{p.first_name} {p.last_name}</p>
      <p className="t-meta truncate">{[p.email || p.phone, p.lists.join(", ")].filter(Boolean).join(" · ")}</p>
    </div>
    {MATCH[p.match] && <span className={`text-[12px] shrink-0 ${p.match === "review" ? "status status-attention" : "t-meta"}`} data-testid={`pco-preview-match-${p.external_id}`}>{MATCH[p.match]}</span>}
  </div>
);

export const ImportPreview = ({ pv, busy, onConfirm, onBack }) => {
  const [q, setQ] = useState("");
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? pv.people.filter((p) => `${p.first_name} ${p.last_name} ${p.email || ""} ${p.lists.join(" ")}`.toLowerCase().includes(t)) : pv.people;
  }, [q, pv]);
  return (
    <div className="mt-6" data-testid="pco-preview">
      <h3 className="t-label">Ready to import</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-3">
        {pv.mode === "lists" && <Stat n={pv.lists} label={pv.lists === 1 ? "List selected" : "Lists selected"} testid="pco-preview-lists" />}
        <Stat n={pv.total} label="people" testid="pco-preview-total" />
        <Stat n={pv.households} label="households" testid="pco-preview-households" />
        {pv.mode === "lists" && <Stat n={pv.on_multiple_lists} label="on more than one List, imported once" testid="pco-preview-multi" />}
      </div>
      {(pv.will_link > 0 || pv.needs_review > 0) && <p className="t-meta mt-3" data-testid="pco-preview-matches">{pv.will_link} will link to people already in Care · {pv.needs_review} will need match review after import</p>}
      {pv.lists_failed.length > 0 && <p className="status status-attention mt-3">Couldn't read: {pv.lists_failed.join(", ")}</p>}
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search this preview" className="field h-11 px-3 mt-5 w-full" data-testid="pco-preview-search" />
      <div className="rule-list border-y border-line mt-3 max-h-[420px] overflow-y-auto">
        {shown.map((p) => <PersonRow key={p.external_id} p={p} />)}
        {!shown.length && <p className="t-meta py-4" data-testid="pco-preview-empty">{pv.total ? "No one matches that search." : "No active people on these Lists."}</p>}
      </div>
      <div className="flex flex-wrap gap-2 mt-5">
        <button disabled={busy || !pv.total} onClick={onConfirm} className="btn btn-primary btn-sm" data-testid="pco-preview-import">{busy ? "Starting…" : `Import ${pv.total} ${pv.total === 1 ? "person" : "people"}`}</button>
        <button onClick={onBack} className="btn btn-outline btn-sm" data-testid="pco-preview-back">Change Lists</button>
      </div>
    </div>
  );
};

export const ImportSummary = ({ s, onReview }) => {
  const navigate = useNavigate();
  const p = s.last_sync.result.people;
  return (
    <div className="mt-5" data-testid="pco-import-summary">
      <p className="t-name-sm">Planning Center connected</p>
      <div className="t-body mt-2 space-y-1">
        <p data-testid="pco-summary-imported">{p.created + p.matched + p.updated} people imported</p>
        <p data-testid="pco-summary-linked">{p.matched} linked to existing Care people</p>
        <p data-testid="pco-summary-review">{p.review} need match review</p>
        <p data-testid="pco-summary-households">{p.households} households synced</p>
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        {s.review_count > 0 && <button onClick={onReview} className="btn btn-primary btn-sm" data-testid="pco-summary-review-btn">Review matches</button>}
        <button onClick={() => navigate("/people")} className="btn btn-outline btn-sm" data-testid="pco-summary-people-btn">Go to People</button>
      </div>
    </div>
  );
};
